import React, { useCallback, useEffect, useState } from "react";
import {
  ChevronLeft,
  ChevronRight,
  PencilSquare,
  Trash3,
  Search,
  PlusLg,
  BoxSeam,
  Grid,
  List,
} from "react-bootstrap-icons";
import Drawer from "../../components/Common/Drawer";
import TooltipWrapper from "../../components/Common/TooltipWrapper";
import { decryptData } from "../../utils/crypto";
import Utils from "../../utils";
import ConfirmModal from "../../components/Common/ConfirmModal";
import FlexibleSkeleton from "../../components/Common/FlexibleSkeleton";
import DesktopTableSkeleton from "../../components/Common/DesktopTableSkeleton";
import { useTranslation } from "react-i18next";
import {
  deleteCategory,
  createCategory,
  updateCategory,
} from "../../services/categoryServices";
import { useSelector } from "react-redux";
import CONFIG from "../../config";
import { notify } from "../../components/Common/Alerts";
import {
  createCustomerStore,
  deleteCustomerStore,
  getCustomerStoreMasterList,
} from "../../services/storeOwnerOrderServices";

const StoreWiseCustomer = () => {
  let auth = useSelector((x) => x.auth.value);
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: "",
    mobile: "",
    status: 1,
    uuid: "",
  });
  const [errors, setErrors] = useState({
    name: "",
    mobile: "",
  });
  const [params, setParams] = useState({
    limit: CONFIG.DATA_LIMIT,
    page: 1,
    search: "",
    is_reload: true,
    store_id: auth.active_store_id,
  });
  const [customerStoreList, setCustomerStoreList] = useState([]);
  const [totalRecord, setTotalRecord] = useState(0);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [loadingPage, setLoadingPage] = useState(false);
  const [deleteShow, setDeleteShow] = useState(false);
  const [deleteId, setDeleteId] = useState("");
  const [btnDeleteLoading, setBtnDeleteLoading] = useState(false);
  const [showOffcanvas, setShowOffcanvas] = useState(false);
  const [filters, setFilters] = useState({ search: "" });
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [skeletonLoading, setSkeletonLoading] = useState(false);
  const [showOffAddcanvas, setShowOffAddcanvas] = useState(false);
  const [btnLoading, setBtnLoading] = useState(false);
  const [viewMode, setViewMode] = useState("grid"); // "grid" | "list"

  const currentPage = params.page;
  const totalPages = Math.ceil(totalRecord / params.limit);
  const startIndex = (currentPage - 1) * params.limit;
  const endIndex = startIndex + params.limit;

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const validateField = (name, value) => {
    switch (name) {
      case "name":
        if (!value) return t("nameIsRequired");
        return "";

      case "mobile":
        if (!value) return t("mobileIsRequired");
        const mobileRegex = /^\d{10}$/;
        if (!mobileRegex.test(value)) {
          return t("enterDigitMobileNumber");
        }
        return "";

      default:
        return "";
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    setErrors((prev) => ({
      ...prev,
      [name]: validateField(name, value),
    }));
  };

  const fetchCustomerList = useCallback(() => {
    if (params.page === 1) {
      setLoading(true); // full page loader
    } else {
      setLoadingPage(true); // pagination loader
    }
    getCustomerStoreMasterList(params)
      .then((response) => {
        const decrypted = decryptData(response.data.data);
        const newData = decrypted.data || [];

        setCustomerStoreList((prev) =>
          params.is_reload ? newData : [...prev, ...newData],
        );

        setTotalRecord(decrypted.total || 0);

        if (newData.length < params.limit) setHasMore(false);
      })
      .finally(() => {
        if (!isMobile) setLoading(false);
        setLoadingPage(false);
        setSkeletonLoading(false);
      });
  }, [params]);

  useEffect(() => {
    fetchCustomerList();
  }, [params]);

  useEffect(() => {
    if (!isMobile) setLoading(false);
    setSkeletonLoading(true);

    // Reset state
    // setCustomerStoreList([]);
    setHasMore(true);

    setParams((prev) => ({
      ...prev,
      page: 1,
      is_reload: true,
    }));
  }, [isMobile]);

  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;

    const isBottom = scrollTop + clientHeight >= scrollHeight - 50;

    if (isBottom && hasMore && !loadingPage) {
      setLoadingPage(true);

      setParams((prev) => ({
        ...prev,
        page: prev.page + 1,
        is_reload: false,
      }));
    }
  };

  const handleDeleteCategory = (e) => {
    e.preventDefault();
    setBtnDeleteLoading(true);
    deleteCustomerStore({
      customer_id: deleteId.customer_id,
      store_id: auth.active_store_id,
    })
      .then((response) => {
        let decrypt = decryptData(response.data.data);
        notify.success(decrypt.message);
        setCustomerStoreList((prev) =>
          prev.filter((item) => item.uuid !== deleteId.uuid),
        );
        setDeleteShow(false);
      })
      .catch((err) => notify.error(Utils.getErrorMessage(err)))
      .finally(() => setBtnDeleteLoading(false));
  };

  const handleParams = (page) => setParams((prev) => ({ ...prev, page }));

  const handleSearch = () => {
    setParams((prev) => ({
      ...prev,
      ...filters,
      page: 1,
      is_reload: true,
    }));
    setShowOffcanvas(false);
  };

  const handleReset = () => {
    setFilters({
      search: "",
    });
    setParams({
      limit: 20,
      page: 1,
      search: "",
      is_reload: true,
    });
    setHasMore(true);
    setLoadingPage(false);
    setShowOffcanvas(false);
  };

  const handleEditOpen = (e, item) => {
    setShowOffAddcanvas(true);
    setFormData({
      name: item?.name,
      mobile: item?.mobile,
      status: item?.status,
      uuid: item?.uuid,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const newErrors = {
      name: validateField("name", formData.name),
      mobile: validateField("mobile", formData.mobile),
    };
    setErrors(newErrors);
    Utils.focusError();

    const hasErrors = Object.values(newErrors).some((err) => err);
    if (hasErrors) return;

    setBtnLoading(true);

    const payload = {
      name: formData.name?.trim(),
      mobile: formData.mobile,
      status: formData.status,
      store_id: auth.active_store_id,
      ...(formData?.uuid && { uuid: formData.uuid }),
    };

    try {
      const response = await createCustomerStore(payload);
      const decrypted = decryptData(response.data.data);
      notify.success(decrypted.message);
      setParams((prev) => ({
        ...prev,
        page: 1,
        is_reload: true,
      }));

      setFormData({
        name: "",
        mobile: "",
        uuid: "",
        status: 1,
      });
      setErrors({ name: "", mobile: "" });
      setShowOffAddcanvas(false);
    } catch (err) {
      notify.error(Utils.getErrorMessage(err));
    } finally {
      setBtnLoading(false);
    }
  };

  const handleAddOpen = () => {
    setShowOffAddcanvas(true);
  };

  const handleAddClose = () => {
    setShowOffAddcanvas(false);
    setFormData({
      name: "",
      mobile: "",
      uuid: "",
      status: 1,
    });
    setErrors({ name: "", mobile: "" });
  };

  const handleDeleteClose = () => {
    setDeleteShow(false);
  };
  const handleDeleteShow = (e, item) => {
    setDeleteId(item);
    setDeleteShow(true);
  };

  return (
    <>
      <div className="h-full flex flex-col animate-fade-up space-y-4 md:space-y-6">
        <div className="flex justify-between items-center px-2">
          <div>
            <h6 className="text-xl md:text-2xl font-black text-slate-800">
              {t("customers")}
            </h6>
            <p className="text-[10px] md:text-xs text-gray-400 font-black uppercase tracking-widest">
              {totalRecord} {t("customerOnboarded")}
            </p>
          </div>

          <div className="flex items-center gap-2">
            <div className="hidden md:flex items-center bg-slate-100 dark:bg-slate-800 rounded-lg p-1">
              {" "}
              <button
                onClick={() => setViewMode("grid")}
                disabled={viewMode === "grid"}
                title={t("switchToGridView")}
                className={`p-2 transition flex items-center justify-center 
                  ${
                    viewMode === "grid"
                      ? "bg-slate-100 text-slate-400 cursor-not-allowed"
                      : "bg-white text-slate-600 hover:bg-orange-100"
                  }`}
              >
                <Grid size={16} />
              </button>
              <button
                onClick={() => setViewMode("list")}
                disabled={viewMode === "list"}
                title={t("switchToListView")}
                className={`
                p-2 transition flex items-center justify-center
                ${
                  viewMode === "list"
                    ? "bg-slate-100 text-slate-400 cursor-not-allowed"
                    : "bg-white text-slate-600 hover:bg-orange-100"
                }`}
              >
                <List size={16} />
              </button>
            </div>
            <button
              className="mobile-filter-btn"
              onClick={() => setShowOffcanvas(true)}
            >
              <Search size={18} />
            </button>

            <button
              className="desktop-filter-btn"
              onClick={() => setShowOffcanvas(true)}
            >
              <Search size={18} />
              <span>{t("filter")}</span>
            </button>

            {Number(auth.user.role_id) === CONFIG.ROLE_OWNER && (
              <button className="add-btn" onClick={handleAddOpen}>
                <PlusLg />
                <span className="hidden md:block">{t("addNew")}</span>
              </button>
            )}
          </div>
        </div>

        {viewMode === "grid" ? (
          <div
            className="flex-1 space-y-3 md:space-y-4 overflow-y-auto pb-4 px-2 md:px-6 scroll-smooth"
            onScroll={handleScroll}
            style={{ marginBottom: "88px" }}
          >
            {skeletonLoading ? (
              <FlexibleSkeleton
                count={params.limit}
                showAvatar={false}
                showName
                showRole
                showContact={false}
                showButtons
              />
            ) : customerStoreList?.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 mt-2">
                {customerStoreList?.map((item) => (
                  <div
                    key={item.uuid}
                    className="bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 rounded-[30px] p-5 shadow-sm hover:shadow-md transition-all flex flex-col gap-5"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex flex-col gap-1">
                        <h4 className="font-bold text-slate-700 dark:text-white text-lg capitalize">
                          {item.name}
                        </h4>

                        <p className="text-sm font-semibold text-slate-500 dark:text-slate-400">
                          {item.country_code} {item.mobile}
                        </p>
                      </div>
                    </div>

                    {Number(auth.user.role_id) === CONFIG.ROLE_OWNER && (
                      <button
                        onClick={(e) => handleDeleteShow(e, item)}
                        className="w-full h-11 rounded-xl bg-red-50 hover:bg-red-100 dark:bg-red-900/20 dark:hover:bg-red-900/30 text-red-500 font-bold text-sm  uppercase tracking-wider flex  items-center justify-center gap-2  transition"
                      >
                        <Trash3 size={16} />
                        {t("delete")}
                      </button>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="no-data">
                <BoxSeam className="no-data-icon" />
                <h3 className="no-data-title">{t("noStoreCategoryFound")}</h3>
                <p className="no-data-text">{t("tryAdjustingFilter")}</p>
                <button onClick={handleReset} className="no-data-btn">
                  {t("resetFilter")}
                </button>
              </div>
            )}

            {loadingPage && (
              <div className="flex justify-center py-4">
                <div className="w-6 h-6 border-2 border-gray-300 border-t-primary rounded-full animate-spin"></div>
              </div>
            )}
          </div>
        ) : (
          <div className="hidden md:flex bg-white dark:bg-slate-900 rounded-[40px] border border-slate-100 dark:border-slate-800 shadow-sm flex-1 flex-col overflow-hidden transition-colors min-h-0">
            <div className="overflow-auto flex-1 custom-scrollbar">
              <table className="w-full text-left border-separate border-spacing-0">
                <thead className="bg-brand-orange sticky top-0 z-10">
                  <tr>
                    <th className="table-header-text first:rounded-tl-[40px]">
                      #
                    </th>
                    <th className="table-header-text">{t("name")}</th>
                    <th className="table-header-text">{t("mobile")}</th>
                    {Number(auth.user.role_id) === CONFIG.ROLE_OWNER && (
                      <th className="table-header-text text-right last:rounded-tr-[40px]">
                        {t("action")}
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50 dark:divide-slate-800">
                  {loading ? (
                    <DesktopTableSkeleton
                      rows={customerStoreList?.length || 5}
                      columns={4}
                    />
                  ) : customerStoreList?.length > 0 ? (
                    customerStoreList?.map((item, i) => (
                      <tr key={item.uuid} className="border-b">
                        <td className="table-header-body font-bold">
                          {item.index}
                        </td>
                        <td className="table-header-body">{item.name}</td>
                        <td className="table-header-body">{item.mobile}</td>
                        {Number(auth.user.role_id) === CONFIG.ROLE_OWNER && (
                          <td className="table-header-body">
                            <div className="flex items-center justify-end gap-2">
                              <TooltipWrapper title="Delete" placement="top">
                                <button
                                  className="delete-btn"
                                  onClick={(e) => handleDeleteShow(e, item)}
                                >
                                  <Trash3 />
                                </button>
                              </TooltipWrapper>
                            </div>
                          </td>
                        )}
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        colSpan={4}
                        className="text-center py-6 text-slate-400 dark:text-slate-500 font-medium"
                      >
                        {t("noStoreCategoryFound")}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {customerStoreList?.length > 0 && (
              <div className="p-4 md:p-6 bg-white dark:bg-slate-900 border-t border-slate-100 dark:border-slate-800 flex flex-col md:flex-row justify-between items-center rounded-b-[40px] transition-colors hidden md:flex">
                <p className="text-sm text-slate-500 dark:text-slate-400 font-medium mb-4 md:mb-0">
                  {t("showing")}{" "}
                  <span className="font-bold text-slate-700 dark:text-slate-200">
                    {startIndex + 1}
                  </span>{" "}
                  {t("to")}{" "}
                  <span className="font-bold text-slate-700 dark:text-slate-200">
                    {Math.min(endIndex, totalRecord)}
                  </span>{" "}
                  {t("of")} {totalRecord}
                </p>

                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleParams(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="p-2 rounded-xl border border-slate-200 dark:border-slate-700 text-slate-500 hover:bg-slate-50 dark:hover:bg-slate-800 disabled:opacity-30 transition-all"
                  >
                    <ChevronLeft size={18} />
                  </button>

                  <div className="hidden md:flex gap-2">
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                      (page) => (
                        <button
                          key={page}
                          onClick={() => handleParams(page)}
                          className={`w-9 h-9 rounded-xl text-sm font-black transition-all ${
                            currentPage === page
                              ? "bg-brand-orange text-white shadow-lg shadow-orange-200"
                              : "text-slate-500 hover:bg-slate-50 dark:hover:bg-slate-800"
                          }`}
                        >
                          {page}
                        </button>
                      ),
                    )}
                  </div>

                  <div className="md:hidden px-4">
                    <span className="text-sm font-black text-brand-orange">
                      {t("page")} {currentPage} {t("of")} {totalPages}
                    </span>
                  </div>

                  <button
                    onClick={() => handleParams(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="p-2 rounded-xl border border-slate-200 dark:border-slate-700 text-slate-500 hover:bg-slate-50 dark:hover:bg-slate-800 disabled:opacity-30 transition-all"
                  >
                    <ChevronRight size={18} />
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      <Drawer
        isOpen={showOffcanvas}
        title={t("filterCategory")}
        description={t("filterYourData")}
        onClose={() => setShowOffcanvas(false)}
        footer={
          <div className="flex gap-3 flex-row justify-end w-full">
            <button className="btn-filter-close" onClick={handleReset}>
              {t("reset")}
            </button>

            <button className="btn-submit-orange" onClick={handleSearch}>
              {t("applyFilters")}
            </button>
          </div>
        }
      >
        <div className="space-y-4 p-2">
          <div className="flex flex-col gap-2">
            <label className="input-label">{t("searchByName")}</label>
            <input
              type="text"
              className="input-field"
              placeholder={t("searchByName")}
              value={filters.search}
              onChange={(e) =>
                setFilters({ ...filters, search: e.target.value })
              }
            />
          </div>
        </div>
      </Drawer>

      <Drawer
        isOpen={showOffAddcanvas}
        title={formData?.uuid ? t("updateCustomer") : t("addCustomer")}
        onClose={handleAddClose}
        footer={
          <div className="flex gap-3 flex-row justify-end w-full">
            <button onClick={handleAddClose} className="btn-filter-close">
              {t("close")}
            </button>

            <button
              type="button"
              form="category-form"
              className="btn-submit-orange"
              onClick={handleSubmit}
              disabled={btnLoading}
            >
              {btnLoading && (
                <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
              )}
              {btnLoading
                ? t("submitting")
                : formData.uuid
                  ? t("update")
                  : t("submit")}
            </button>
          </div>
        }
      >
        <div className="flex flex-col gap-2">
          <label htmlFor="name" className="input-label">
            {t("name")} <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            name="name"
            className={`input-field ${errors.name ? "input-error" : ""}`}
            value={formData?.name}
            onChange={handleChange}
          />
          {errors.name && <span className="text-danger">{errors.name}</span>}
        </div>

        <div className="flex flex-col gap-2">
          <label htmlFor="mobile" className="input-label">
            {t("mobile")} <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            inputMode="numeric"
            placeholder={t("enterMobile")}
            className={`input-field ${errors.mobile ? "input-error" : ""}`}
            value={formData?.mobile || ""}
            onChange={handleChange}
            name="mobile"
          />
          {errors.mobile && (
            <span className="text-danger">{errors.mobile}</span>
          )}
        </div>
      </Drawer>

      <ConfirmModal
        open={deleteShow}
        onClose={handleDeleteClose}
        onConfirm={handleDeleteCategory}
        loading={btnDeleteLoading}
        titleKey={t("deleteCategory")}
        descriptionKey={t("deleteConfirmation", { name: deleteId?.name })}
        confirmText="Yes, Delete"
        icon={<Trash3 size={40} className="text-red-500" />}
      />
    </>
  );
};

export default StoreWiseCustomer;
